/**
 * EmptyCanaryDetail Component
 *
 * Empty state shown on the canary detail page when there are no detections yet.
 * Guides the user through embedding the pixel and verifying the setup.
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { Card, Button, Badge } from '@/components/ui'

type EmptyCanaryDetailProps = {
  canaryId: string
}

export function EmptyCanaryDetail({ canaryId }: EmptyCanaryDetailProps) {
  const queryClient = useQueryClient()
  const [verifyResult, setVerifyResult] = useState<{
    success: boolean
    confidenceScore?: number
  } | null>(null)

  const verifyMutation = useMutation({
    mutationFn: () => api.verifyCanary(canaryId),
    onSuccess: (data) => {
      setVerifyResult({ success: true, confidenceScore: data.confidenceScore })
      void queryClient.invalidateQueries({ queryKey: ['canary-detections', canaryId] })
    },
    onError: () => {
      setVerifyResult({ success: false })
    },
  })

  return (
    <Card title="No Detections Yet">
      <div className="text-center py-6 space-y-4">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100">
          <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>

        <div>
          <p className="text-gray-600 mb-2">
            This canary hasn't recorded any detections yet.
          </p>
          <p className="text-sm text-gray-500">
            Make sure the embed code is placed on your page, then use the button below to send a test detection.
          </p>
        </div>

        {/* Verify button */}
        <div>
          <Button
            variant="primary"
            onClick={() => { verifyMutation.mutate(); }}
            isLoading={verifyMutation.isPending}
            disabled={verifyMutation.isPending}
          >
            Verify Canary
          </Button>
        </div>

        {/* Result */}
        {verifyResult && (
          <div className={`p-3 rounded-lg text-sm ${
            verifyResult.success
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {verifyResult.success ? (
              <div className="flex items-center justify-center gap-2">
                <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Test detection recorded successfully.</span>
                {verifyResult.confidenceScore !== undefined && (
                  <Badge variant="success">Score: {verifyResult.confidenceScore}</Badge>
                )}
              </div>
            ) : (
              <span>Verification failed. Please check the canary configuration.</span>
            )}
          </div>
        )}
      </div>
    </Card>
  )
}
